/*
 * <sticky-product-story-section> — behavior for sections/section-sticky-product-story.liquid.
 * Scoped to `this` instead of `document`, so connectedCallback/disconnectedCallback
 * handle Shopify theme-editor re-renders cleanly (no global init flag needed).
 */
(function () {
  if (customElements.get('sticky-product-story-section')) return;

  customElements.define(
    'sticky-product-story-section',
    class StickyProductStorySection extends HTMLElement {
      connectedCallback() {
        const grid = this.querySelector('.sticky-product-story__grid');
        if (!grid) return;

        this._cleanup = [];
        this._observers = [];

        const on = (target, type, handler, opts) => {
          target.addEventListener(type, handler, opts);
          this._cleanup.push(() => target.removeEventListener(type, handler, opts));
        };

        this._initPurchaseOptions();
        this._initStickyAtcBar(on);
        this._initStorySlider(on);

        // ponytail: left-column text swap is a sequence, not independent
        // per-screen toggles — a screen shorter than the viewport (e.g. the
        // square learn-more image) would have its *bottom* cross the trigger
        // before the next screen even starts, flipping back to the default
        // text mid-scroll. Walk the screens in DOM order and treat whichever
        // one's trigger most recently fired as active; that state holds
        // until the *next* screen's trigger fires.
        //
        // The trigger is a real gap check: bottom edge of the
        // currently-shown panel's actual text vs. top edge of the incoming
        // .sticky-product-story__screen, swap once that gap is down to GAP
        // px. .sticky-product-story__left is `min-height: calc(100vh -
        // header - 80px)` with `justify-content: center` (section-sticky-
        // product-story.css), so the panel box itself is viewport-tall —
        // the text sits vertically centered inside it, not flush with the
        // panel's own bottom edge. What matters is the text's bottom, i.e.
        // the panel's last child, measured relative to the panel's top so
        // it's stable regardless of scroll position; display:none panels
        // are forced visible (inline style beats the stylesheet) just long
        // enough to read real layout, no visual flash since no paint
        // happens between the read and the revert.
        const GAP = 15;
        const getStickyTop = () => {
          const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
          return headerHeight + 40;
        };
        const measureContentOffset = (panel) => {
          const hidden = getComputedStyle(panel).display === 'none';
          if (hidden) panel.style.setProperty('display', 'flex', 'important');
          const content = panel.lastElementChild || panel;
          const offset = content.getBoundingClientRect().bottom - panel.getBoundingClientRect().top;
          if (hidden) panel.style.removeProperty('display');
          return offset;
        };
        const sequenceToggle = (segments) => {
          segments = segments
            .map(([fromPanelSelector, screenSelector, className]) => ({
              fromPanel: this.querySelector(fromPanelSelector),
              screen: this.querySelector(screenSelector),
              className,
            }))
            .filter((seg) => seg.fromPanel && seg.screen);
          if (!segments.length) return;

          const remeasure = () => segments.forEach((seg) => { seg.contentOffset = measureContentOffset(seg.fromPanel); });
          remeasure();

          let ticking = false;
          const update = () => {
            ticking = false;
            const stickyTop = getStickyTop();
            let activeIndex = -1;
            segments.forEach((seg, i) => {
              const gap = seg.screen.getBoundingClientRect().top - (stickyTop + seg.contentOffset);
              if (gap <= GAP) activeIndex = i;
            });
            segments.forEach((seg, i) => grid.classList.toggle(seg.className, i === activeIndex));
          };
          const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(update);
          };
          on(window, 'scroll', onScroll, { passive: true });
          on(window, 'resize', () => {
            remeasure();
            onScroll();
          });
          update();
        };

        this.querySelectorAll('[data-compare-slider]').forEach((container) => {
          const handle = container.querySelector('[data-compare-handle]');
          let dragging = false;

          const setPosition = (clientX) => {
            const rect = container.getBoundingClientRect();
            const pct = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
            container.style.setProperty('--reveal-x', pct + '%');
          };

          on(handle, 'pointerdown', (event) => {
            dragging = true;
            handle.setPointerCapture(event.pointerId);
          });
          on(container, 'pointermove', (event) => {
            if (!dragging) return;
            setPosition(event.clientX);
          });
          on(handle, 'pointerup', () => { dragging = false; });
          on(handle, 'pointercancel', () => { dragging = false; });
        });

        // ponytail: the left/right sticky swap is a desktop-only trick (mobile
        // falls back to plain stacked CSS via `position: static` + flex `order`
        // in the max-width:767px media query) — skip wiring it up at all on
        // mobile instead of letting the scroll-linked class toggles fight the
        // static layout's own reflows.
        if (window.matchMedia('(min-width: 768px)').matches) {
          sequenceToggle([
            ['.sticky-product-story__left:not(.sticky-product-story__left--results):not(.sticky-product-story__left--learn-more)', '[data-results-screen]', 'is-results-active'],
            ['.sticky-product-story__left--results', '[data-learn-more-screen]', 'is-learn-more-active'],
          ]);

          const footerScreen = this.querySelector('[data-footer-screen]');
          if (footerScreen) {
            const observer = new IntersectionObserver(
              ([entry]) => grid.classList.toggle('is-footer-active', entry.isIntersecting),
              { rootMargin: '-50% 0px -50% 0px' }
            );
            observer.observe(footerScreen);
            this._observers.push(observer);

            const target = grid.querySelector('[data-footer-color-target]');
            {
            let ticking = false;
            const updateReveal = () => {
              ticking = false;
              const screenRect = footerScreen.getBoundingClientRect();

              if (target) {
                const targetRect = target.getBoundingClientRect();
                const height = targetRect.height || 1;
                // ponytail: clip to the real intersection of text vs. video, not just
                // a bottom-anchored percentage — so on the way in the color creeps up
                // from the bottom (video arriving from below), and on the way out it
                // recedes from the bottom too (video's own bottom edge scrolling past
                // the text first), instead of wrongly peeling off from the top.
                const topPx = Math.min(Math.max(screenRect.top - targetRect.top, 0), height);
                const bottomPx = Math.min(Math.max(screenRect.bottom - targetRect.top, 0), height);
                target.style.setProperty('--footer-text-top', (topPx / height) * 100 + '%');
                target.style.setProperty('--footer-text-bottom', (bottomPx / height) * 100 + '%');
              }

              // ponytail: unlike the plain title/subtitle text, these rows have
              // their own borders — clipping a pixel-slice through the middle of
              // a bordered box (the earlier approach) cuts the border in half and
              // looks broken. Each row flips as a whole instead, right as the
              // video's edge crosses its vertical center, with the color/border
              // transition below doing the easing instead of a scroll-driven clip.
              grid.querySelectorAll('.sticky-product-story__purchase-method').forEach((row) => {
                const rowRect = row.getBoundingClientRect();
                const center = rowRect.top + rowRect.height / 2;
                row.classList.toggle('is-video-covered', center >= screenRect.top && center <= screenRect.bottom);
              });
            };
            const onScroll = () => {
              if (ticking) return;
              ticking = true;
              requestAnimationFrame(updateReveal);
            };

            on(window, 'scroll', onScroll, { passive: true });
            on(window, 'resize', onScroll);
            updateReveal();
            }
          }
        }

        on(this, 'click', (event) => {
          // ponytail: the popup dialog itself (open/close wiring, scroll lock) is owned
          // by <product-showcase-section> - this only opens it, reusing the one instance
          // already on the page instead of duplicating a second dialog here.
          const popupTrigger = event.target.closest('[data-subscription-popup-trigger]');
          if (popupTrigger) {
            const dialog = document.getElementById(popupTrigger.dataset.subscriptionPopupTrigger);
            if (dialog) {
              dialog.showModal();
              document.documentElement.classList.add('product-showcase-popup-open');
              document.body.classList.add('product-showcase-popup-open');
              requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
            }
            return;
          }

          const opener = event.target.closest('[data-ingredients-modal-open]');
          if (opener) {
            const dialog = document.getElementById(opener.getAttribute('data-ingredients-modal-open'));
            dialog?.showModal();
            return;
          }

          const closer = event.target.closest('[data-ingredients-modal-close]');
          if (closer) {
            closer.closest('dialog')?.close();
            return;
          }

          if (event.target.tagName === 'DIALOG' && event.target.classList.contains('sticky-product-story__modal')) {
            event.target.close();
          }

          const nav = event.target.closest('[data-result-prev], [data-result-next]');
          if (nav) {
            const pagination = nav.closest('[data-result-pagination]');
            const texts = grid.querySelectorAll('[data-result-text]');
            const index = Array.from(texts).indexOf(grid.querySelector('.is-active[data-result-text]'));
            const delta = nav.hasAttribute('data-result-prev') ? -1 : 1;
            const nextIndex = (index + delta + texts.length) % texts.length;

            texts.forEach((text, i) => text.classList.toggle('is-active', i === nextIndex));
            pagination.querySelector('[data-result-current]').textContent = nextIndex + 1;
            // ponytail: the before/after slider is independent of the slides —
            // switching text never moves it, only dragging does.
          }
        });
      }

      _initPurchaseOptions() {
        const options = this.querySelectorAll('[data-purchase-option]');
        const planInput = this.querySelector('[data-selling-plan-input]');
        if (!options.length || !planInput) return;

        const applyActive = () => {
          options.forEach((option) => {
            const radio = option.querySelector('[data-purchase-radio]');
            option.classList.toggle('is-active', radio.checked);
          });
          this._updateAtcPrice();
        };

        options.forEach((option) => {
          const radio = option.querySelector('[data-purchase-radio]');
          option.addEventListener('click', () => {
            radio.checked = true;
            planInput.value = radio.value;
            applyActive();
          });
        });
      }

      // First-screen slider. Swiping is native CSS scroll-snap (see
      // section-sticky-product-story.css) — this only wires the arrows and hides
      // whichever one has nowhere left to go. Renders only when the product's
      // custom.story_gallery metafield holds at least one image.
      _initStorySlider(on) {
        const slider = this.querySelector('[data-story-slider]');
        if (!slider) return;

        const track = slider.querySelector('[data-story-slides]');
        const prev = slider.querySelector('[data-story-slider-prev]');
        const next = slider.querySelector('[data-story-slider-next]');

        const update = () => {
          // 1px slack: scrollLeft is fractional at fractional container widths.
          prev.disabled = track.scrollLeft <= 1;
          next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
        };

        on(prev, 'click', () => track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' }));
        on(next, 'click', () => track.scrollBy({ left: track.clientWidth, behavior: 'smooth' }));
        on(track, 'scroll', update, { passive: true });
        on(window, 'resize', update);
        update();
      }

      // Page-wide sticky add-to-cart: shown whenever the section's own ATC button is
      // off screen. The bar lives on <body> so this section's `isolation: isolate`
      // (and any later section's stacking context) can't paint over it; the button
      // keeps submitting the section's form through its `form` attribute.
      _initStickyAtcBar(on) {
        const bar = this.querySelector('[data-atc-bar]');
        if (!bar) return;

        document.body.appendChild(bar);
        this._atcBar = bar;

        // Watch every add-to-cart button on the page (this section's, the product
        // showcase's, whatever else the template renders), not just our own — the bar
        // is only for when the user has none of them on screen. Deferred to
        // DOMContentLoaded because sections further down the page aren't parsed yet
        // when this custom element upgrades.
        const start = () => {
          const buttons = Array.from(document.querySelectorAll('[data-add-to-cart]'));
          if (!buttons.length) return;

          // ponytail: hit-test the button's center instead of IntersectionObserver —
          // at the end of the story the sticky column slides *under* the next section,
          // so the button is still "intersecting" while the user can't see it.
          const isOnScreen = (button) => {
            const rect = button.getBoundingClientRect();
            if (!rect.width || !rect.height) return false;
            if (rect.bottom <= 0 || rect.top >= window.innerHeight) return false;
            if (rect.right <= 0 || rect.left >= window.innerWidth) return false;

            const x = Math.min(Math.max(rect.left + rect.width / 2, 1), window.innerWidth - 1);
            const y = Math.min(Math.max(rect.top + rect.height / 2, 1), window.innerHeight - 1);
            const topmost = document.elementFromPoint(x, y);
            return Boolean(topmost) && (topmost === button || button.contains(topmost));
          };

          // Hide once the footer comes into view — a fixed bar would sit on top of it.
          const footer = document.querySelector('footer');
          const footerReached = () =>
            Boolean(footer) && footer.getBoundingClientRect().top < window.innerHeight;

          let ticking = false;
          const update = () => {
            ticking = false;
            bar.classList.toggle('is-visible', !footerReached() && !buttons.some(isOnScreen));
          };
          const schedule = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(update);
          };

          on(window, 'scroll', schedule, { passive: true });
          on(window, 'resize', schedule);
          update();
        };

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', start, { once: true });
        } else {
          start();
        }
      }

      _updateAtcPrice() {
        const priceEl = this.querySelector('[data-atc-price]');
        const checkedRadio = this.querySelector('[data-purchase-radio]:checked');
        if (!priceEl || !checkedRadio) return;

        const isSubscription = checkedRadio.value !== '';
        const source = isSubscription
          ? this.querySelector('[data-purchase-price="plan"]')
          : this.querySelector('[data-purchase-price="onetime"]');
        if (source && source.textContent.trim()) {
          priceEl.textContent = source.textContent.trim();
        }
      }

      disconnectedCallback() {
        if (this._atcBar) this._atcBar.remove();
        this._atcBar = null;
        this._cleanup.forEach((fn) => fn());
        this._observers.forEach((observer) => observer.disconnect());
        this._cleanup = [];
        this._observers = [];
      }
    }
  );
})();
